import { DEFAULT_SETTINGS } from './inspection/constants';

// Pure presentation helpers: entry points (badges / buttons) in the reservation lists, status labels, error mapping.
// Message keys are keys of mobile_languages/web/<lang>.json.

const DAY_MS = 24 * 60 * 60 * 1000;

// Reservation dates reach the browser as epoch milliseconds (string) or ISO strings.
export function toDayNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  let date;
  if (typeof value === 'number' || (typeof value === 'string' && /^\d{9,}$/.test(value.trim()))) date = new Date(Number(value));
  else date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / DAY_MS);
}

const localDayNumber = (now) => {
  const date = new Date(now === undefined ? Date.now() : now);
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS);
};

export const ENTRY_STATES = Object.freeze({
  closed: { labelKey: 'inspEntryClosed', tone: 'neutral', icon: 'clock' },
  todo: { labelKey: 'inspEntryTodo', tone: 'warning', icon: 'edit' },
  waiting: { labelKey: 'inspEntryWaiting', tone: 'neutral', icon: 'clock' },
  to_validate: { labelKey: 'inspEntryToValidate', tone: 'warning', icon: 'clock' },
  validated: { labelKey: 'inspEntryValidated', tone: 'success', icon: 'ok' },
  dispute: { labelKey: 'inspEntryDispute', tone: 'danger', icon: 'alert' }
});

// Entry point of the check-in / check-out in a reservation list or in the trip details.
// reservation: { reservationState, checkIn, checkOut, checkinStatus, checkoutStatus, hasDispute, globalStatus, inspectionEnabled }
// userType: 'owner' | 'renter'. Returns null when nothing must be shown (feature off, not a confirmed reservation...).
// The calendar test is only an indication: the server decides the real window (the page shows its answer).
export function getInspectionEntry({ reservation, type, userType, now, checkoutWindowDays }) {
  if (!reservation || (type !== 'checkin' && type !== 'checkout')) return null;
  // null = not a party (never happens in the lists) ; false = switched off by the admin.
  if (reservation.inspectionEnabled !== true) return null;
  const state = reservation.reservationState;
  if (state !== 'approved' && state !== 'completed') return null;

  const link = `/${type}/${reservation.id === undefined ? reservation.reservationId : reservation.id}`;
  const status = (type === 'checkin' ? reservation.checkinStatus : reservation.checkoutStatus) || 'not_done';
  const today = localDayNumber(now);
  const start = toDayNumber(reservation.checkIn);
  const end = toDayNumber(reservation.checkOut);
  const isOwner = userType === 'owner';
  const make = (key) => ({ type, state: key, link, ...ENTRY_STATES[key] });

  if (type === 'checkout' && (reservation.hasDispute && reservation.globalStatus === 'dispute')) return make('dispute');
  if (status === 'validated') return make('validated');
  if (status === 'to_validate') return make('to_validate');
  if (status === 'draft') return make('todo');

  // Nothing done yet.
  if (type === 'checkin') {
    if (state === 'completed' || (end !== null && today > end)) return null;
    if (start !== null && today < start) return make('closed');
    return make(isOwner ? 'todo' : 'waiting');
  }
  const windowDays = Number.isFinite(checkoutWindowDays) ? checkoutWindowDays : DEFAULT_SETTINGS.checkoutWindowDays;
  if (end !== null && today > end + windowDays) return null;
  const checkinDone = reservation.checkinStatus === 'validated';
  if (end !== null && today < end && !checkinDone) return make('closed');
  return make(isOwner ? 'waiting' : 'todo');
}

// Badge of a stored status (not_done / draft / to_validate / validated).
export function formatInspectionStatus(status) {
  switch (status) {
    case 'validated': return { labelKey: 'inspStatusValidated', tone: 'success', icon: 'ok' };
    case 'to_validate': return { labelKey: 'inspStatusToValidate', tone: 'warning', icon: 'clock' };
    case 'draft': return { labelKey: 'inspStatusDraft', tone: 'neutral', icon: 'edit' };
    default: return { labelKey: 'inspStatusNotDone', tone: 'neutral', icon: 'clock' };
  }
}

// Global status derived by the server (helpers/inspection/globalStatus.js).
export function globalStatusKey(status) {
  const keys = {
    upcoming: 'inspGlobalUpcoming',
    in_progress: 'inspGlobalInProgress',
    to_validate: 'inspGlobalToValidate',
    damage_declared: 'inspGlobalDamage',
    dispute: 'inspGlobalDispute',
    completed: 'inspGlobalCompleted',
    dispute_closed: 'inspGlobalDisputeClosed',
    cancelled: 'inspGlobalCancelled'
  };
  return keys[status] || 'inspGlobalUpcoming';
}

export function globalStatusTone(status) {
  return { dispute: 'danger', damage_declared: 'danger', completed: 'success', dispute_closed: 'success', in_progress: 'info', to_validate: 'warning', cancelled: 'neutral' }[status] || 'info';
}

// Admin tabs (M4): derived from the fields of the list, no extra request.
export const ADMIN_TABS = Object.freeze(['all', 'in_progress', 'upcoming', 'completed', 'dispute', 'damage']);

export function matchesAdminTab(reservation, tab) {
  const status = reservation && reservation.globalStatus;
  switch (tab) {
    case 'in_progress': return ['in_progress', 'to_validate', 'damage_declared'].includes(status);
    case 'upcoming': return status === 'upcoming';
    case 'completed': return ['completed', 'dispute_closed'].includes(status);
    case 'dispute': return status === 'dispute' || Boolean(reservation && reservation.hasDispute && status !== 'dispute_closed');
    case 'damage': return Boolean(reservation && reservation.hasDamage === true);
    default: return true;
  }
}

export function countAdminTabs(reservations) {
  const counts = {};
  ADMIN_TABS.forEach((tab) => { counts[tab] = (reservations || []).filter((item) => matchesAdminTab(item, tab)).length; });
  return counts;
}

// Period filter (page level): keeps the reservations whose stay overlaps [from, to] ('YYYY-MM-DD', both optional).
export function matchesPeriod(reservation, from, to) {
  if (!from && !to) return true;
  const start = toDayNumber(reservation && reservation.checkIn);
  const end = toDayNumber(reservation && reservation.checkOut);
  if (start === null || end === null) return false;
  const fromDay = from ? toDayNumber(from) : null;
  const toDay = to ? toDayNumber(to) : null;
  if (fromDay !== null && end < fromDay) return false;
  if (toDay !== null && start > toDay) return false;
  return true;
}

// Why the window is not open (getInspection.windowReason) -> message key.
export function windowReasonKey(reason) {
  const keys = {
    inspectionDisabled: 'inspWindowDisabled',
    notYetOpen: 'inspWindowNotYetOpen',
    closed: 'inspWindowClosed',
    reservationState: 'inspWindowReservationState',
    paymentNotCompleted: 'inspWindowReservationState',
    locked: 'inspWindowLocked'
  };
  return keys[reason] || 'inspWindowClosed';
}

// Upload failures (HTTP status of POST /inspection/photos) -> message key.
export function uploadErrorKey(httpStatus, code) {
  if (code === 'unsupported') return 'inspUploadUnsupported';
  if (code === 'tooLarge' || httpStatus === 413) return 'inspUploadTooLarge';
  if (code === 'limit' || httpStatus === 409) return 'inspUploadConflict';
  if (httpStatus === 400 || httpStatus === 415) return 'inspUploadInvalid';
  if (httpStatus === 401 || httpStatus === 403) return 'inspUploadForbidden';
  if (httpStatus === 429) return 'inspUploadBusy';
  if (httpStatus === 503) return 'inspUploadUnavailable';
  return 'inspUploadFailed';
}

// Decision type -> label key.
export function decisionLabelKey(type) {
  return {
    release_all: 'inspDecisionReleaseAll',
    retain_part: 'inspDecisionRetainPart',
    retain_all: 'inspDecisionRetainAll',
    no_action: 'inspDecisionNoAction',
    request_indemnity: 'inspDecisionIndemnity'
  }[type] || 'inspDecisionNoAction';
}

export function disputeStatusKey(status) {
  return { open: 'inspDisputeStatusOpen', in_review: 'inspDisputeStatusInReview', resolved: 'inspDisputeStatusResolved', closed: 'inspDisputeStatusClosed' }[status] || 'inspDisputeStatusOpen';
}

// Timeline of the admin detail (M5): steps with their date, in order. Pure from the fields of the screen.
export function buildTimeline({ reservation, checkin, checkout, dispute }) {
  const steps = [];
  const push = (key, at, tone) => steps.push({ key, at: at || null, tone: tone || 'ok' });
  push('inspStepBooked', reservation && reservation.createdAt);
  if (checkin && (checkin.status === 'validated')) push('inspStepCheckin', checkin.lockedAt || checkin.updatedAt);
  else push('inspStepCheckin', null, 'pending');
  const started = Boolean(checkin && checkin.status === 'validated') || (reservation && reservation.reservationState === 'completed');
  push('inspStepInProgress', null, started ? 'ok' : 'pending');
  if (checkout) push('inspStepCheckout', checkout.status === 'validated' ? (checkout.lockedAt || checkout.updatedAt) : checkout.createdAt, checkout.status === 'validated' ? 'ok' : 'pending');
  else push('inspStepCheckout', null, 'pending');
  if (checkout && checkout.hasDamage === true) push('inspStepDamage', checkout.createdAt, 'danger');
  if (dispute) push('inspStepDispute', dispute.createdAt, 'danger');
  const closed = Boolean(dispute ? (dispute.status === 'resolved' || dispute.status === 'closed') : (reservation && ['closed', 'dispute_closed'].includes(reservation.closureState)));
  push('inspStepClosed', closed ? (dispute && dispute.closedAt) || null : null, closed ? 'ok' : 'pending');
  return steps;
}
